import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AuthContext = createContext(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshTimerRef = useRef(null);

  const clearAuth = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const scheduleTokenRefresh = useCallback((expiresIn) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    // Refresh 60 seconds before expiry
    const refreshMs = Math.max((expiresIn - 60) * 1000, 30000);
    refreshTimerRef.current = setTimeout(async () => {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) return;
      try {
        const response = await axios.post(`${API_URL}/api/auth/refresh`, {
          refresh_token: refreshToken,
        });
        const { access_token, refresh_token: newRefresh, expires_in } = response.data;
        localStorage.setItem('token', access_token);
        if (newRefresh) localStorage.setItem('refresh_token', newRefresh);
        setToken(access_token);
        if (expires_in) scheduleTokenRefresh(expires_in);
      } catch {
        clearAuth();
        window.location.href = '/';
      }
    }, refreshMs);
  }, [clearAuth]);

  useEffect(() => {
    loadStoredAuth();
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadStoredAuth = async () => {
    try {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
        // Render UI immediately using cached user; validate token in the background.
        // This unblocks first paint of dashboards (saves ~100-200ms of /auth/me RTT).
        setIsLoading(false);

        // Background validation — kicks off without blocking initial render
        (async () => {
          try {
            const response = await axios.get(`${API_URL}/api/auth/me`, {
              headers: { Authorization: `Bearer ${storedToken}` }
            });
            setUser(response.data);
            localStorage.setItem('user', JSON.stringify(response.data));
            scheduleTokenRefresh(1800);
          } catch {
            const refreshToken = localStorage.getItem('refresh_token');
            if (refreshToken) {
              try {
                const refreshRes = await axios.post(`${API_URL}/api/auth/refresh`, {
                  refresh_token: refreshToken,
                });
                const { access_token, refresh_token: newRefresh, expires_in } = refreshRes.data;
                localStorage.setItem('token', access_token);
                if (newRefresh) localStorage.setItem('refresh_token', newRefresh);
                setToken(access_token);

                const meRes = await axios.get(`${API_URL}/api/auth/me`, {
                  headers: { Authorization: `Bearer ${access_token}` }
                });
                setUser(meRes.data);
                localStorage.setItem('user', JSON.stringify(meRes.data));
                if (expires_in) scheduleTokenRefresh(expires_in);
              } catch {
                clearAuth();
                window.location.href = '/';
              }
            } else {
              clearAuth();
              window.location.href = '/';
            }
          }
        })();
        return;
      }
    } catch {
      clearAuth();
    }
    setIsLoading(false);
  };

  const login = async (email, password, recaptchaToken = null) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      email,
      password,
      recaptcha_token: recaptchaToken,
    });

    if (response.data.requires_2fa) {
      const error = new Error('2FA_REQUIRED');
      error.response = { status: 200, data: response.data };
      throw error;
    }

    const { access_token, refresh_token, expires_in, user: userData } = response.data;

    localStorage.setItem('token', access_token);
    if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('user', JSON.stringify(userData));

    setToken(access_token);
    setUser(userData);
    if (expires_in) scheduleTokenRefresh(expires_in);
    
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, userData);
    const { access_token, refresh_token, expires_in, user: newUser } = response.data;

    localStorage.setItem('token', access_token);
    if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('user', JSON.stringify(newUser));

    setToken(access_token);
    setUser(newUser);
    if (expires_in) scheduleTokenRefresh(expires_in);
    
    return newUser;
  };

  const logout = () => {
    clearAuth();
  };

  const setAuthFromToken = (accessToken, userData, refreshToken, expiresIn) => {
    localStorage.setItem('token', accessToken);
    if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(accessToken);
    setUser(userData);
    if (expiresIn) scheduleTokenRefresh(expiresIn);
  };

  const forgotPassword = async (email) => {
    const response = await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
    return response.data.message;
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const refreshUser = async () => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) return;
    
    const response = await axios.get(`${API_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${storedToken}` }
    });
    setUser(response.data);
    localStorage.setItem('user', JSON.stringify(response.data));
    return response.data;
  };

  return (
    <AuthContext.Provider value={{ user, token, isLoading, login, register, logout, forgotPassword, updateUser, setAuthFromToken, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
